import {
  Checkout,
  InspectionType,
  PayerType,
  PaymentMethod
} from "@comparaonline/cicl-funnel-schema";
import { OwnerType } from "@comparaonline/cicl-funnel-schema/dist/enums/checkout/owner-type";
import { getNextAvailableNumber } from "../../../queries/getNextAvailableNumber";
import { CarUseTypeCl } from "@comparaonline/quote-forms-schema/dist/enums/car-insurance-cl/carUseType";

import { getCommune } from "../../../queries/getCommune";
import { getInsuranceTypeByOffer } from "./getInsuranceTypeByOffer";
import { getPaymentMethosAllowedByInsuranceType } from "./getPaymentMethosAllowedByInsuranceType";
import { isCompanyRut } from "./nationalId";
import { getThirdPartySponsors, isValidThirdPartySponsor } from "./thirdPartySponsors";
import { CheckoutStatus } from "@comparaonline/cico-funnel-schema";
import { isExpiredOffer } from "../utils/validators/checkout";

const carIsNew = (year: number): boolean => new Date().getFullYear() === year;

const getCommuneAndRegion = (communeId: number, apolloClient): Promise<Partial<Checkout>> =>
  apolloClient
    .query({ query: getCommune })
    .then(({ data }) => data.commune.find(commune => commune.id === communeId))
    .then(commune => ({ county: commune.name, region: commune.region.full_name }));

const getFirstPaymentMethodAllowed = (offer, isThirdPartySponsor): PaymentMethod => {
  const insuranceType = getInsuranceTypeByOffer(offer);
  const paymentMethods = getPaymentMethosAllowedByInsuranceType(
    insuranceType,
    offer,
    isThirdPartySponsor
  );
  return paymentMethods[0].name;
};

export const getInitialCheckout = async (
  quote,
  apolloClient,
  offer
): Promise<Partial<Checkout>> => {
  const { county, region } = await getCommuneAndRegion(quote.countyId, apolloClient);
  const saleId = await getNextAvailableNumber("cicl_sale-id");
  const carDealerName = quote && quote.carDealerData && quote.carDealerData.name;
  const thirdPartySponsors = await getThirdPartySponsors();
  const isThirdPartySponsor = isValidThirdPartySponsor(carDealerName, thirdPartySponsors);
  const isCompany = isCompanyRut(quote.nationalId);
  const checkout = {
    ...(isExpiredOffer(quote.expirationDate) && { status: CheckoutStatus.FAILURE }),
    car: {
      brand: quote.car.brandName,
      model: quote.car.modelName,
      isNew: carIsNew(quote.car.modelYear),
      plate: quote.car.plate || "",
      year: quote.car.modelYear
    },
    device: {
      useResidenceAddress: true
    },
    inspection: {
      inspectionType: InspectionType.Home,
      useResidenceAddress: true
    },
    owner: {
      ...(isCompany
        ? { businessName: quote.businessName, type: OwnerType.Company }
        : {
            birthdate: quote.birthdate,
            lastName: quote.lastName,
            name: quote.firstName,
            type: OwnerType.Person
          }),
      email: quote.email,
      nationalId: quote.nationalId,
      phone: quote.phone.replace(/\s/g, "")
    },
    payer: {
      ...(isCompany
        ? { name: quote.businessName, nationalId: quote.nationalId, type: PayerType.Company }
        : { type: PayerType.Owner })
    },
    payment: {
      method: getFirstPaymentMethodAllowed(offer, isThirdPartySponsor)
    },
    saleId: saleId || 0,
    residenceAddress: {
      county,
      region
    },
    vehicleUseType: quote.carUseType || CarUseTypeCl.PRIVATE
  };

  return checkout;
};
